import ActionMenuV2 from "components/ActionMenuV2";
import message from "components/CustomMessage";
import { CheckIcon, PlayIcon } from "components/Icons";
import TableContainer from "components/TableContainer";
import { PermissionSetMap } from "constants/RBACConstants";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import GenericIntegrationAPI, {
  BaseIntegrationConfig,
  IntegrationConfigListResponse,
} from "restAPI/GenericIntegrationAPI";
import ModalUtil from "utils/modalUtil";
import { Sorters, getFilterProps } from "utils/tableUtils";

export interface GenericIntegrationListingProps<
  T extends BaseIntegrationConfig,
> {
  integrationType: string;
  configurationRef?: React.RefObject<{ refresh: () => void }>;
  onUpdateConfigComponent?: () => void;
  onConfigCountChange?: (count: number) => void;
  customColumns?: any[];
  FormComponent: React.ComponentType<{
    configData?: T;
    onUpdateConfigComponent: () => void;
    configList?: T[];
  }>;
  name?: string;
  showTriggerScan?: boolean;
}

export interface GenericIntegrationListingRef {
  refresh: () => void;
}

const baseActionItems = [
  { type: "edit", permission: PermissionSetMap.EDIT_TOOL_CONFIG },
  {
    type: "validate",
    text: "Validate",
    icon: <CheckIcon />,
    permission: PermissionSetMap.EDIT_TOOL_CONFIG,
  },
  { type: "delete", permission: PermissionSetMap.DELETE_TOOL },
];

const triggerScanActionItem = {
  type: "trigger-scan",
  text: "Trigger Scan",
  icon: <PlayIcon className="" />,
  permission: PermissionSetMap.EDIT_TOOL_CONFIG,
};

function GenericIntegrationListingInner<T extends BaseIntegrationConfig>(
  {
    integrationType,
    configurationRef,
    onUpdateConfigComponent,
    onConfigCountChange,
    customColumns = [],
    FormComponent,
    showTriggerScan = false,
  }: GenericIntegrationListingProps<T>,
  ref: React.ForwardedRef<GenericIntegrationListingRef>,
) {
  const [configurations, setConfigurations] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const onConfigCountChangeRef = useRef(onConfigCountChange);
  onConfigCountChangeRef.current = onConfigCountChange;

  const fetchConfigurations = useCallback(() => {
    setLoading(true);
    const { promise, cancel } =
      GenericIntegrationAPI.getConfigurations<T>(integrationType);
    promise
      .then((response: IntegrationConfigListResponse<T> | any) => {
        const data = response?.data || response;
        const configs = data?.configurations || data?.content || [];
        setConfigurations(configs);
        onConfigCountChangeRef.current?.(configs.length);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    return cancel;
  }, [integrationType]);

  useEffect(() => {
    return fetchConfigurations();
  }, [fetchConfigurations]);

  const handleRefresh = useCallback(() => {
    fetchConfigurations();
    onUpdateConfigComponent?.();
  }, [fetchConfigurations, onUpdateConfigComponent]);

  useImperativeHandle(
    configurationRef || ref,
    () => ({ refresh: handleRefresh }),
    [handleRefresh],
  );

  const onDelete = useCallback(
    (id: number) => {
      const { promise } = GenericIntegrationAPI.deleteConfiguration(id);
      promise.then(() => {
        message.success("Configuration deleted successfully.");
        handleRefresh();
      });
      return promise;
    },
    [handleRefresh],
  );

  const onValidate = useCallback(
    (id: number) => {
      const closeLoading = message.loading("Validating configuration...");
      const { promise } = GenericIntegrationAPI.validateConfiguration(id);
      promise
        .then((result) => {
          if (result.success) {
            message.success("Configuration validated successfully.");
          } else {
            message.warn(result.message || "Validation failed.");
          }
          handleRefresh();
        })
        .finally(closeLoading);
    },
    [handleRefresh],
  );

  const onTriggerScan = useCallback(
    (id: number) => {
      const closeLoading = message.loading("Triggering scan...");
      const { promise } = GenericIntegrationAPI.triggerScan(
        id,
        integrationType,
      );
      promise
        .then((result) => {
          if (result.success) {
            message.success(result.message || "Scan triggered successfully.");
          } else {
            message.warn(result.message || "Failed to trigger scan.");
          }
        })
        .finally(closeLoading);
    },
    [integrationType],
  );

  const openConfigForm = useCallback(
    (configData?: T) => {
      ModalUtil.show({
        content: (
          <FormComponent
            configData={configData}
            onUpdateConfigComponent={handleRefresh}
            configList={configurations}
          />
        ),
      });
    },
    [handleRefresh, configurations, FormComponent],
  );

  const actionItems = useMemo(
    () =>
      showTriggerScan
        ? [triggerScanActionItem, ...baseActionItems]
        : baseActionItems,
    [showTriggerScan],
  );

  const onActionClick = useCallback(
    async (_: unknown, record: { name: string; data: T }) => {
      switch (record.name) {
        case "edit":
          openConfigForm(record.data);
          break;
        case "validate":
          onValidate(record.data.id);
          break;
        case "trigger-scan":
          onTriggerScan(record.data.id);
          break;
        case "delete":
          await onDelete(record.data.id);
          break;
      }
    },
    [openConfigForm, onDelete, onValidate, onTriggerScan],
  );

  const columns = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "name",
        sorter: Sorters.STRING("name"),
        ...getFilterProps({ dataIndex: "name", dataSource: configurations }),
      },
      ...customColumns,
      {
        title: "Status",
        dataIndex: "status",
        width: 100,
      },
      {
        title: "Action",
        key: "action",
        dataIndex: "id",
        width: 50,
        render: (_id: number, obj: T) => (
          <ActionMenuV2
            items={actionItems}
            onClick={onActionClick}
            data={obj}
            byPassConfirmation={false}
          />
        ),
      },
    ],
    [configurations, customColumns, onActionClick, actionItems],
  );

  return (
    <TableContainer
      loading={loading}
      columns={columns}
      dataSource={configurations}
      defaultSort={{ column: "createdAt", order: "descend" }}
      rowKey="id"
    />
  );
}

const GenericIntegrationListing = React.forwardRef(
  GenericIntegrationListingInner,
) as <T extends BaseIntegrationConfig>(
  props: GenericIntegrationListingProps<T> & {
    ref?: React.ForwardedRef<GenericIntegrationListingRef>;
  },
) => React.ReactElement;

export default GenericIntegrationListing;
